import clsx from 'clsx';

import { AlertRuleRow } from '@/react/portainer/observability/types';
import { formatThreshold } from '@/react/portainer/observability/utils';
import { AlertSeverityBadge } from '@/react/portainer/observability/AlertSeverityBadge';
import {
  getAlertRuleCategoryLabel,
  resolveAlertRuleCategory,
} from '@/react/portainer/observability/AlertsView/alertRuleCategory';

import { DetailsTable } from '@@/DetailsTable';
import { Badge } from '@@/Badge';
import { Card } from '@@/primitives/Card';

interface Props {
  alertRule: AlertRuleRow;
}

export function AlertRuleInfo({ alertRule }: Props) {
  const tiers = alertRule.tiers ?? [];
  const hasTierSummary = !!(alertRule.useTiers && tiers.length > 0);
  const hasThresholdSummary =
    !hasTierSummary &&
    alertRule.conditionOperator !== undefined &&
    alertRule.threshold !== undefined;
  const category = resolveAlertRuleCategory(alertRule);

  return (
    <>
      <DetailsTable dataCy="alert-rule-info-table">
        <DetailsTable.Row label="Name">{alertRule.name}</DetailsTable.Row>
        <DetailsTable.Row label="Category">
          <Badge type="info" className="text-xs">
            {getAlertRuleCategoryLabel(category)}
          </Badge>
        </DetailsTable.Row>
        <DetailsTable.Row label="Type">
          <Badge type="muted" className="text-xs">
            {alertRule.isInternal ? 'System' : 'Custom'}
          </Badge>
        </DetailsTable.Row>
        <DetailsTable.Row label="Description">
          {alertRule.description}
        </DetailsTable.Row>
        {!hasTierSummary && (
          <DetailsTable.Row label="Severity">
            <AlertSeverityBadge severity={alertRule.severity} />
          </DetailsTable.Row>
        )}
        <DetailsTable.Row label="Status">
          <Badge type={alertRule.enabled ? 'success' : 'muted'}>
            {alertRule.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </DetailsTable.Row>
        {alertRule.updatedAt ? (
          <DetailsTable.Row label="Last updated">
            {alertRule.updatedAt}
          </DetailsTable.Row>
        ) : (
          <DetailsTable.Row label="Created">
            {alertRule.createdAt}
          </DetailsTable.Row>
        )}
      </DetailsTable>
      {(hasTierSummary || hasThresholdSummary) && (
        <>
          <div className="row">
            <div className="col-sm-12">
              <h4>{hasTierSummary ? 'Severity Thresholds' : 'Rule Summary'}</h4>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              {hasTierSummary ? (
                <>
                  {!alertRule.enabled && (
                    <p className="mb-2 text-xs text-gray-6">
                      Rule is globally disabled. Tier statuses below reflect
                      saved configuration.
                    </p>
                  )}
                  <div
                    data-cy="alert-rule-tier-summary"
                    aria-disabled={!alertRule.enabled}
                    className={clsx({
                      'rounded-sm border border-gray-4 bg-gray-2 p-2 opacity-70 th-highcontrast:border-white th-highcontrast:bg-gray-10 th-dark:border-gray-8 th-dark:bg-gray-iron-10':
                        !alertRule.enabled,
                    })}
                  >
                    <DetailsTable
                      dataCy="tier-thresholds-table"
                      headers={['Severity', 'Threshold', 'Status']}
                    >
                      {tiers.map((tier) => (
                        <DetailsTable.Row
                          key={tier.severity}
                          ariaLabel={tier.severity}
                          label={
                            <AlertSeverityBadge severity={tier.severity} />
                          }
                          columns={[
                            <Badge
                              key="status"
                              type={tier.enabled ? 'success' : 'muted'}
                            >
                              {tier.enabled ? 'Enabled' : 'Disabled'}
                            </Badge>,
                          ]}
                        >
                          {formatThreshold(
                            tier.threshold,
                            alertRule.metricType
                          )}
                        </DetailsTable.Row>
                      ))}
                    </DetailsTable>
                  </div>
                </>
              ) : (
                <Card.Container variant="filled">
                  <Card.Body>
                    <p className="text-sm">
                      {'Triggers when '}
                      <strong>{alertRule.conditionOperator}</strong>{' '}
                      <strong>
                        {formatThreshold(
                          alertRule.threshold!,
                          alertRule.metricType
                        )}
                      </strong>
                      {alertRule.useDurationAsFor === false ? (
                        <>
                          {' over a '}
                          <strong>{alertRule.duration}</strong>
                          {'-minute window'}
                        </>
                      ) : (
                        <>
                          {' for '}
                          <strong>{alertRule.duration}</strong>
                          {' minutes'}
                        </>
                      )}
                      {' → severity is '}
                      <AlertSeverityBadge severity={alertRule.severity} />
                    </p>
                  </Card.Body>
                </Card.Container>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
